import { CloseOutlined, DownloadOutlined } from "@ant-design/icons";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { PDFViewer, PDFViewerRef } from "@embedpdf/react-pdf-viewer";
import { Button, Space, Spin, Tooltip, Typography } from "antd";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface CapacitorPdfViewerProps {
  isOpen?: boolean;
  onClose?: () => void;
  pdfUrl?: string;
  pdfBlob?: Blob | null;
  fileName?: string;
  title?: string;
}

const { Text } = Typography;

const DISABLED_CATEGORIES = [
  "annotation",
  "form",
  "print",
  "export",
  "annotation",
  "redaction",
  "document",
  "panel-comment",
  "panel-sidebar",
  "tools",
  "selection",
  "history",
  "insert",
  "security",
];

const CONTROL_BUTTON_STYLE: React.CSSProperties = {
  width: 40,
  height: 40,
  minWidth: 40,
  fontSize: 16,
};

const getSafeFileName = (value?: string) => {
  const clean = (value || "document")
    .replace(/\.pdf$/i, "")
    .replace(/[^a-z0-9-_]+/gi, "_")
    .replace(/^_+|_+$/g, "");

  return `${clean || "document"}.pdf`;
};

const bytesToBase64 = (bytes: Uint8Array) => {
  let binary = "";
  const chunkSize = 0x8000;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }

  return window.btoa(binary);
};

const base64ToBytes = (base64Data: string) => {
  const cleanBase64 = base64Data.includes(",")
    ? base64Data.split(",")[1]
    : base64Data;
  const binary = window.atob(cleanBase64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
};

const blobToBytes = async (blob: Blob) =>
  new Uint8Array(await blob.arrayBuffer());

const bytesToArrayBuffer = (bytes: Uint8Array): ArrayBuffer => {
  const arrayBuffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(arrayBuffer).set(bytes);
  return arrayBuffer;
};

const fetchPdfBytes = async (pdfUrl: string, isNative: boolean) => {
  if (isNative) {
    const response = await CapacitorHttp.get({
      url: pdfUrl,
      responseType: "arraybuffer",
      connectTimeout: 30000,
      readTimeout: 60000,
      headers: {
        Accept: "application/pdf,*/*",
      },
    });

    if (response.status < 200 || response.status >= 300) {
      throw new Error(`PDF request failed with status ${response.status}`);
    }

    if (typeof response.data !== "string") {
      throw new Error("PDF response did not return base64 data");
    }

    return base64ToBytes(response.data);
  }

  const response = await fetch(pdfUrl, {
    headers: {
      Accept: "application/pdf,*/*",
    },
  });

  if (!response.ok) {
    throw new Error(`PDF request failed with status ${response.status}`);
  }

  return new Uint8Array(await response.arrayBuffer());
};

const CapacitorPdfViewer: React.FC<CapacitorPdfViewerProps> = ({
  isOpen = true,
  onClose = () => {},
  pdfUrl,
  pdfBlob,
  fileName,
  title = "Document Preview",
}) => {
  const viewerRef = useRef<PDFViewerRef>(null);
  const blobObjectUrlRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const isNative = Capacitor.isNativePlatform();
  const safeFileName = useMemo(
    () => getSafeFileName(fileName || title),
    [fileName, title],
  );

  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);

  useEffect(() => {
    if (blobObjectUrlRef.current) {
      window.URL.revokeObjectURL(blobObjectUrlRef.current);
      blobObjectUrlRef.current = null;
    }

    if (pdfUrl) {
      setResolvedSrc(pdfUrl);
      return;
    }

    if (pdfBlob) {
      const objectUrl = window.URL.createObjectURL(pdfBlob);
      blobObjectUrlRef.current = objectUrl;
      setResolvedSrc(objectUrl);
      return;
    }

    setResolvedSrc(null);

    return () => {
      if (blobObjectUrlRef.current) {
        window.URL.revokeObjectURL(blobObjectUrlRef.current);
        blobObjectUrlRef.current = null;
      }
    };
  }, [pdfUrl, pdfBlob]);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError("");
  }, [isOpen, resolvedSrc]);

  const handleClose = useCallback(() => {
    if (blobObjectUrlRef.current) {
      window.URL.revokeObjectURL(blobObjectUrlRef.current);
      blobObjectUrlRef.current = null;
    }
    onClose();
  }, [onClose]);

  const loadBytes = useCallback(async () => {
    if (pdfBlob) {
      return blobToBytes(pdfBlob);
    }

    if (pdfUrl) {
      return fetchPdfBytes(pdfUrl, isNative);
    }

    throw new Error("PDF data is missing");
  }, [isNative, pdfBlob, pdfUrl]);

  const handleDownload = useCallback(async () => {
    try {
      setDownloading(true);
      const bytes = await loadBytes();

      if (isNative) {
        const savedFile = await Filesystem.writeFile({
          path: `${Date.now()}_${safeFileName}`,
          data: bytesToBase64(bytes),
          directory: Directory.Cache,
        });

        await Share.share({
          title,
          text: title,
          url: savedFile.uri,
          dialogTitle: "Save PDF",
        });
      } else {
        const objectUrl = window.URL.createObjectURL(
          new Blob([bytesToArrayBuffer(bytes)], { type: "application/pdf" }),
        );
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = safeFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      }
    } catch (err) {
      console.error("PDF download error:", err);
    } finally {
      setDownloading(false);
    }
  }, [isNative, loadBytes, safeFileName, title]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        display: "flex",
        flexDirection: "column",
        background: "#FFFFFF",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          minHeight: 58,
          paddingTop: "calc(10px + env(safe-area-inset-top, 0px))",
          paddingRight: "calc(12px + env(safe-area-inset-right, 0px))",
          paddingBottom: 10,
          paddingLeft: "calc(12px + env(safe-area-inset-left, 0px))",
          background: "#FFFFFF",
          borderBottom: "1px solid #E7E7E7",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          flexShrink: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Text
            strong
            ellipsis
            style={{
              display: "block",
              maxWidth: "40vw",
              color: "#231F20",
              fontSize: 15,
              lineHeight: "20px",
            }}
          >
            {title}
          </Text>
        </div>
        <Space size={8} wrap={false}>
          <Tooltip title="Download">
            <Button
              shape="circle"
              style={CONTROL_BUTTON_STYLE}
              icon={<DownloadOutlined />}
              loading={downloading}
              onClick={handleDownload}
            />
          </Tooltip>
          <Tooltip title="Close">
            <Button
              shape="circle"
              style={CONTROL_BUTTON_STYLE}
              type="primary"
              icon={<CloseOutlined />}
              onClick={handleClose}
            />
          </Tooltip>
        </Space>
      </div>
      <div
        style={{
          flex: 1,
          position: "relative",
          minHeight: 0,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          background: "#FFFFFF",
        }}
      >
        {loading && !error && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(82, 86, 89, 0.92)",
              pointerEvents: "none",
            }}
          >
            <Spin size="large" tip="Loading PDF..." />
          </div>
        )}
        {error ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Space direction="vertical" size={12}>
              <Text style={{ color: "#FFFFFF" }}>{error}</Text>
              <Button onClick={handleDownload} loading={downloading}>
                DOWNLOAD PDF
              </Button>
            </Space>
          </div>
        ) : (
          resolvedSrc && (
            <PDFViewer
              ref={viewerRef}
              style={{ height: "100%", width: "100%" }}
              config={{
                src: resolvedSrc,
                theme: { preference: "light" },
                tabBar: "never",
                disabledCategories: DISABLED_CATEGORIES,
              }}
              onReady={() => setLoading(false)}
            />
          )
        )}
      </div>
    </div>
  );
};

export default CapacitorPdfViewer;
